// src/admin/pages/Inventory.tsx
import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { Search, Filter, Download, AlertTriangle, ArrowDown, ArrowUp } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Product as BaseProduct, Category } from '../../types';
import { adminApi } from '../../utils/api';

// Update this line to use the environment variable
const backendBaseURL = import.meta.env.VITE_API_BASE_URL;
const backendBaseImagesURL = import.meta.env.VITE_API_BASE_IMAGES_URL;

// Extended Product type for inventory management
interface InventoryProduct extends BaseProduct {
  stockStatus: 'low' | 'normal';
  lastUpdated: string;
}

function getFullImageUrl(url: string | undefined): string {
  if (!url) return 'https://via.placeholder.com/80?text=No+Image';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return backendBaseImagesURL + url;
}

export const Inventory = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [products, setProducts] = useState<InventoryProduct[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch categories and products from backend
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await adminApi.categories.getAll();
        setCategories(response.data);
      } catch (err: any) {
        console.error(err);
        setError(err.message || 'Failed to fetch categories.');
        toast.error(err.message || 'Failed to fetch categories.');
      }
    };

    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await adminApi.products.getAll();
        // Enhance products with stockStatus and lastUpdated
        const enhancedProducts = response.data.map(product => ({
          ...product,
          stockStatus: (product.stock <= product.min_stock ? 'low' : 'normal') as 'low' | 'normal',
          lastUpdated: product.updated_at ? new Date(product.updated_at).toLocaleDateString() : 'N/A'
        }));

        setProducts(enhancedProducts);
      } catch (err: any) {
        console.error(err);
        setError(err.message || 'Failed to fetch products.');
        toast.error(err.message || 'Failed to fetch products.');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
    fetchProducts();
  }, []);

  // Helper to get category name by category_id
  const getCategoryName = (categoryId: number): string => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Uncategorized';
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch =
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.sku?.toLowerCase() || '').includes(searchQuery.toLowerCase());

    const matchesCategory = categoryFilter === 'all' || getCategoryName(product.category_id) === categoryFilter;

    const matchesStock =
      stockFilter === 'all' ||
      (stockFilter === 'low' && product.stockStatus === 'low') ||
      (stockFilter === 'normal' && product.stockStatus === 'normal');

    return matchesSearch && matchesCategory && matchesStock;
  });

  const getStockStatusColor = (status: string) => {
    switch (status) {
      case 'low':
        return 'bg-red-100 text-red-600';
      case 'normal':
        return 'bg-green-100 text-green-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const handleStockAdjustment = async (productId: string | number, adjustment: number) => {
    // Find the product to adjust
    const product = products.find(p => p.id === productId);
    if (!product) {
      toast.error('Product not found.');
      return;
    }

    const newStock = product.stock + adjustment;

    if (newStock < 0) {
      toast.error('Stock cannot be negative.');
      return;
    }

    try {
      const response = await adminApi.products.update(productId.toString(), { stock: newStock });

      // Update local state
      setProducts(prev =>
        prev.map(p =>
          p.id === productId
            ? {
                ...p,
                stock: newStock,
                stockStatus: newStock <= p.min_stock ? 'low' : 'normal',
                lastUpdated: new Date().toLocaleDateString()
              }
            : p
        )
      );

      toast.success('Stock updated successfully.');
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Error updating stock.');
    }
  };

  return (
    <AdminLayout>
      <div className="h-screen flex flex-col">
        <div className="flex-none p-6 space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold text-gray-800">Inventory Management</h2>
            <button
              onClick={() => toast('Export functionality coming soon!')}
              className="bg-pink-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-pink-600 transition-colors"
            >
              <Download className="h-5 w-5" />
              Export Inventory
            </button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="text-sm text-gray-500">Total Products</div>
              <div className="text-2xl font-semibold mt-1">{products.length}</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="text-sm text-gray-500">Low Stock Items</div>
              <div className="text-2xl font-semibold mt-1 text-red-500">
                {products.filter(p => p.stockStatus === 'low').length}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="text-sm text-gray-500">Out of Stock</div>
              <div className="text-2xl font-semibold mt-1 text-red-500">
                {products.filter(p => p.stock === 0).length}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="text-sm text-gray-500">Categories</div>
              <div className="text-2xl font-semibold mt-1">
                {categories.length}
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search inventory..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>

            <div className="flex items-center gap-2 bg-white p-2 rounded-lg border">
              <Filter className="h-5 w-5 text-gray-400" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="flex-1 border-none focus:ring-0"
              >
                <option key="all-categories" value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category.id} value={category.name}>{category.name}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2 bg-white p-2 rounded-lg border">
              <AlertTriangle className="h-5 w-5 text-gray-400" />
              <select
                value={stockFilter}
                onChange={(e) => setStockFilter(e.target.value)}
                className="flex-1 border-none focus:ring-0"
              >
                <option key="all-stock" value="all">All Stock Levels</option>
                <option key="low-stock" value="low">Low Stock</option>
                <option key="normal-stock" value="normal">Normal Stock</option>
              </select>
            </div>
          </div>
        </div>

        {/* Inventory Table */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-auto px-6 pb-6">
            {loading ? (
              <div className="flex justify-center items-center h-full">
                <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12"></div>
              </div>
            ) : error ? (
              <div className="flex justify-center items-center h-full text-red-500">
                <p>{error}</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Current Stock
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Min Stock
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Updated
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredProducts.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <img
                              src={
                                product.images && product.images.length > 0
                                  ? getFullImageUrl(product.images[0].url)
                                  : 'https://via.placeholder.com/80?text=No+Image'
                              }
                              alt={product.name}
                              className="h-10 w-10 rounded-lg object-cover"
                            />
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{product.name}</div>
                              <div className="text-sm text-gray-500">SKU: {product.sku || 'N/A'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {getCategoryName(product.category_id)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {product.stock}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {product.min_stock}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${getStockStatusColor(product.stockStatus)}`}>
                            {product.stockStatus === 'low' ? 'Low Stock' : 'Normal'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {product.lastUpdated}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleStockAdjustment(product.id, 1)}
                              className="p-1 text-green-600 hover:bg-green-50 rounded"
                              title="Increase Stock"
                            >
                              <ArrowUp className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleStockAdjustment(product.id, -1)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              title="Decrease Stock"
                            >
                              <ArrowDown className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* If no products found */}
                {filteredProducts.length === 0 && !loading && (
                  <div className="p-6 text-center text-gray-500">
                    No products match your search and filter criteria.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loader CSS */}
      <style>{`
        .loader {
          border-top-color: #3498db;
          animation: spinner 1.5s linear infinite;
        }

        @keyframes spinner {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </AdminLayout>
  );
};
